import React, { useMemo } from 'react';
import parse from 'html-react-parser';
import { highlightMatch } from '../utils/highlightMatch';

/**
 * HighlightedText Component
 *
 * Flexible text highlighting component that supports:
 * 1. Elasticsearch highlights (pre-highlighted HTML snippets)
 * 2. Dynamic query highlighting (highlights all occurrences of a search term)
 * 3. Plain text with optional truncation
 *
 * @param {string} text - The original text content
 * @param {string} [highlightQuery] - Search query to highlight (will highlight all occurrences)
 * @param {string[]} [highlight] - Elasticsearch highlight array (HTML snippets with <mark> tags)
 * @param {string} className - Additional CSS classes
 * @param {number} maxLength - Truncate text to this length (applies to plain text only)
 */
export default function HighlightedText({
  text,
  highlightQuery,
  highlight,
  className = '',
  maxLength,
}) {
  const content = useMemo(() => {
    // 1️⃣ Elasticsearch highlight (highest priority)
    // ES returns pre-highlighted HTML snippets; use directly
    if (highlight && Array.isArray(highlight) && highlight.length > 0) {
      return { type: 'html', content: highlight[0] };
    }

    // 2️⃣ Get display text (with optional truncation)
    const displayText = maxLength && text?.length > maxLength
      ? text.substring(0, maxLength) + '...'
      : text;

    // 3️⃣ Query-based highlighting
    if (highlightQuery?.trim()) {
      const html = highlightMatch(displayText, highlightQuery);
      return { type: 'html', content: html };
    }

    // 4️⃣ Plain text (no highlighting)
    return { type: 'text', content: displayText };
  }, [text, highlightQuery, highlight, maxLength]);

  // Render based on content type
  if (content.type === 'html') {
    return <span className={className}>{parse(content.content)}</span>;
  }

  // Plain text - render as text node (no HTML parsing)
  return <span className={className}>{content.content}</span>;
}
